import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { toastVariant, useToast } from '__components/ToastProvider';
import { monitoringExceptionHelper } from 'instrumentation/helpers';
import { getOriginalErrorStatusCode } from 'services/AxiosService';
import { previewEndedMessage } from 'ts-components/Plugins/businessApps/helpers';
import { useCurrentUserId } from 'hooks/useCurrentUserId';
import type { PreviewEndedEvent } from 'ts-components/Plugins/businessApps/types';
import { PLUGINS } from 'queries/query-keys';
import PluginService from 'services/PluginService';
import { useBusinessPluginApps } from 'ts-components/Plugins/useBusinessPluginApps';

const PREVIEW_ENDED_TAG = 'preview_ended';

type Notice = { businessPluginAppId: string; event: PreviewEndedEvent };

const noticeKey = ({ event }: Notice) =>
  `${event.resource_path}@${event.updated}`;

// A notice replaced by another toast isn't dismissed, so it shows again on the next visit.
export const PreviewEndedToasts = () => {
  const { t } = useTranslation();
  const [showToast] = useToast();
  const queryClient = useQueryClient();
  const currentUserId = useCurrentUserId();
  const { data: businessPluginApps } = useBusinessPluginApps();

  const appIdsWithNotifications = useMemo(
    () =>
      (businessPluginApps ?? [])
        .filter((bpa) => bpa.notification_count)
        .map((bpa) => bpa.id),
    [businessPluginApps]
  );

  const combine = useCallback(
    (results: { data?: { events?: PreviewEndedEvent[] } }[]) =>
      results.flatMap(({ data }, index): Notice[] =>
        (data?.events ?? [])
          .filter((event) => event.tag === PREVIEW_ENDED_TAG)
          .map((event) => ({
            businessPluginAppId: appIdsWithNotifications[index],
            event,
          }))
      ),
    [appIdsWithNotifications]
  );
  const notices = useQueries({
    queries: appIdsWithNotifications.map((id) => ({
      queryKey: PLUGINS.GET_EVENTS(id),
      queryFn: () => PluginService.getEvents(id),
      throwOnError: false,
    })),
    combine,
  });

  const [closedKeys, setClosedKeys] = useState<Set<string>>(new Set());
  const isShowing = useRef(false);

  useEffect(() => {
    const next = notices.find((notice) => !closedKeys.has(noticeKey(notice)));
    if (isShowing.current || !next) {
      return;
    }

    const refetchNotices = () => {
      queryClient.invalidateQueries({
        queryKey: PLUGINS.GET_EVENTS(next.businessPluginAppId),
      });
      queryClient.invalidateQueries({
        queryKey: PLUGINS.GET_BUSINESS_PLUGIN_APPS,
      });
    };
    const onClose = () => {
      isShowing.current = false;
      setClosedKeys((keys) => new Set(keys).add(noticeKey(next)));
      PluginService.dismissNotifications(
        next.businessPluginAppId,
        next.event.resource_path
      ).then(refetchNotices, (error: unknown) => {
        // Already dismissed
        if (getOriginalErrorStatusCode(error) === 404) {
          refetchNotices();
        } else {
          monitoringExceptionHelper(error);
        }
      });
    };
    isShowing.current = true;
    showToast({
      message: previewEndedMessage(t, next.event.context, currentUserId),
      variant: toastVariant.ALERT,
      autohide: false,
      onClose,
    });
  }, [notices, closedKeys, currentUserId, queryClient, showToast, t]);

  return null;
};
